import React, { Component } from 'react';
import { Alert, Avatar, Badge, Button, Icon, Select } from 'antd';
import moment from 'moment';
import { inject } from 'mobx-react';
import debounce from 'lodash/debounce';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import computeAge from 'core/utils/computeAge';
import Spacing from 'components/common/Spacing';
import { SELECT_REFETCH_DELAY } from 'core/uiConstants';
import withMyContacts from 'graphql/hocs/profile/withMyContacts';
import withProviderEncounters from 'graphql/hocs/encounter/withProviderEncountersNew';
import withCallsListUpdate from 'graphql/hocs/encounter/withCallsListUpdate';
import withEndCall from 'graphql/hocs/encounter/withEndCall';
import withStartCall from 'graphql/hocs/encounter/withStartCall';
import withEndEncounter from 'graphql/hocs/encounter/withEndEncounter';
import withMyCalls from 'graphql/hocs/encounter/withMyCalls';
import DropdownListWrapper from './DropdownListWrapper';

const CallItemWrapper = styled.div`
  border-top: 1px solid #ccc;
  padding: 10px 0;

  .call-item-info {
    display: flex;

    .avatar {
      display: flex;
      width: 50px;
      flex-direction: column;
      justify-content: center;
    }
    .info {
      display: flex;
      flex: 1;
      justify-content: space-between;

      .info-details {
        .name {
          font-weight: 500;
        }

        .details {
          color: #8392a5;
        }
        .action-buttons {
          margin-top: 5px;
          .ant-btn {
            margin-right: 10px;
          }
        }
      }

      .info-time {
        display: flex;
        flex-direction: column;
        justify-content: center;
        font-size: 10px;
        font-style: italic;
        color: #8392a5;
      }
    }
  }
  .confirm-delete-card {
    display: flex;
    justify-content: space-between;
    margin-top: 10px;
    margin-left: 50px;
    padding: 8px 12px;
    background-color: #fffbe6;
    border: 1px solid #ffe58f;
    border-radius: 4px;
    color: rgba(0, 0, 0, 0.65);

    .message {
      display: flex;
      flex-direction: column;
      justify-content: center;
    }

    .action-buttons {
      .ant-btn {
        margin-left: 4px;
      }
    }
  }
`;
/* eslint-disable react/prefer-stateless-function */
@withMyContacts
@withStartCall
@withEndCall
@withEndEncounter
@withMyCalls
@withProviderEncounters
@withCallsListUpdate
@inject('authStore')
class CallsDropdown extends Component {
  static propTypes = {
    authStore: PropTypes.object.isRequired,
    callsListUpdate: PropTypes.object,
    onGetMyCalls: PropTypes.func.isRequired,
    onGetMyContacts: PropTypes.func.isRequired,
    onGetProviderEncounters: PropTypes.func.isRequired,
    onStartCall: PropTypes.func.isRequired,
    onEndCall: PropTypes.func.isRequired,
    onEndEncounter: PropTypes.func.isRequired,
    onUpdateMyCallsQueryCacheOnAddCall: PropTypes.func.isRequired,
    onUpdateMyCallsQueryCacheOnEndCall: PropTypes.func.isRequired,
    onUpdateEncQCacheOnEndEnc: PropTypes.func.isRequired,
  };

  static defaultProps = {
    callsListUpdate: null,
  };

  state = {
    calls: [],
    confirmDeleteId: '',
    contacts: [],
    encounters: [],
    filteredContacts: [],
    isLoadingCalls: true,
    isLoadingEncounters: true,
    isLoadingStartCall: false,
    startCallErrorMessage: '',
    value: undefined,
  };

  async componentDidMount() {
    const { authStore, onGetMyContacts } = this.props;
    this.loadCalls();
    if (authStore.role === 'provider') {
      this.loadEncounters();
    }
    const contacts = await onGetMyContacts();
    this.sliceContacts(contacts);
    this.setState({ contacts, isLoadingCalls: false });
  }

  componentDidUpdate(prevProps) {
    const { callsListUpdate: prevCallsListUpdate } = prevProps;
    const { callsListUpdate, authStore } = this.props;
    const accountId = authStore.myProfile._id;
    if (
      (!prevCallsListUpdate && callsListUpdate) ||
      (callsListUpdate &&
        prevCallsListUpdate &&
        callsListUpdate.callsListUpdate.updatedAt !== prevCallsListUpdate.callsListUpdate.updatedAt)
    ) {
      if (
        callsListUpdate.callsListUpdate.type === 'new' &&
        callsListUpdate.callsListUpdate.call.caller._id !== accountId
      ) {
        this.handleAddCallFromSubscription(callsListUpdate.callsListUpdate.call);
      }
    }
  }

  handleAddCallFromSubscription = call => {
    const { onUpdateMyCallsQueryCacheOnAddCall } = this.props;
    const { calls } = this.state;
    const isCallExisting = calls.find(({ _id }) => call._id === _id);
    if (!isCallExisting) {
      onUpdateMyCallsQueryCacheOnAddCall(call);
      this.loadCalls();
    }
  };

  handleClickConfirmEndCall = _id => {
    const {
      onEndCall,
      onEndEncounter,
      onUpdateMyCallsQueryCacheOnEndCall,
      onUpdateEncQCacheOnEndEnc,
    } = this.props;
    const { calls } = this.state;
    const call = calls.find(c => c._id === _id);
    if (call) {
      const variables = { input: { _id } };
      onEndCall({ variables });
      onUpdateMyCallsQueryCacheOnEndCall(_id);
      this.loadCalls();
    } else {
      const variables = { input: { _id } };
      onEndEncounter({ variables });
      onUpdateEncQCacheOnEndEnc(_id);
      this.loadEncounters();
    }
    this.setState({ confirmDeleteId: '' });
  };

  handleClickCancelEndCall = () => {
    this.setState({ confirmDeleteId: '' });
  };

  handleClickEndCall = _id => {
    this.setState({ confirmDeleteId: _id });
  };

  handleClickCall = () => {
    const { onStartCall, onUpdateMyCallsQueryCacheOnAddCall, authStore } = this.props;
    const { calls, value } = this.state;
    if (!value) {
      return;
    }

    // see if there is an existing call with the contact client-side first
    let hasExistingCall = false;
    const accountId = authStore.myProfile._id;
    for (let i = 0; i < calls.length; i += 1) {
      const receiverIds = calls[i].receivers.map(({ _id }) => _id);
      if (calls[i].caller._id === accountId && receiverIds.includes(value)) {
        hasExistingCall = true;
        break;
      } else if (calls[i].caller._id === value && receiverIds.includes(accountId)) {
        hasExistingCall = true;
        break;
      }
    }

    if (hasExistingCall) {
      this.setState({
        startCallErrorMessage: 'You have an active call with the selected contact.',
      });
      return;
    }

    this.setState({ isLoadingStartCall: true }, async () => {
      const variables = { input: { receiverId: value } };
      let result;
      try {
        result = await onStartCall({ variables });
        onUpdateMyCallsQueryCacheOnAddCall(result.data.startCall);
        this.loadCalls();
        this.setState({ startCallErrorMessage: '' });
      } catch (error) {
        // do nothing
      }
      if (!result) {
        this.setState({
          startCallErrorMessage: 'You have an active call with the selected contact.',
        });
      }
      this.setState({ isLoadingStartCall: false });
    });
  };

  handleClickOpenInfo = _id => {
    const { authStore } = this.props;
    if (authStore.role === 'provider') {
      window.open(`/provider/info/${_id}`, '_blank');
    }
  };

  loadCalls = () => {
    const { onGetMyCalls } = this.props;
    this.setState({ isLoadingCalls: true }, async () => {
      const data = await onGetMyCalls();
      this.setState({ calls: data.nodes, isLoadingCalls: false });
    });
  };

  loadEncounters = () => {
    const { onGetProviderEncounters } = this.props;
    this.setState({ isLoadingEncounters: true }, async () => {
      const data = await onGetProviderEncounters();
      this.setState({ encounters: data.nodes, isLoadingEncounters: false });
    });
  };

  sliceContacts = filteredContacts => {
    let slicedContacts = [];
    if (filteredContacts.length > 0) {
      slicedContacts = filteredContacts.slice(0, Math.min(10, filteredContacts.length));
    }
    this.setState({ filteredContacts: slicedContacts });
  };

  handleChange = value => {
    this.setState({ value, startCallErrorMessage: '' });
  };

  handleSearch = searchString => {
    const { contacts } = this.state;
    const filteredContacts = contacts.filter(c =>
      c.name.toLowerCase().includes(searchString.toLowerCase()),
    );
    this.sliceContacts(filteredContacts);
  };

  renderConfirmDelete(_id) {
    const { confirmDeleteId } = this.state;
    if (confirmDeleteId !== _id) {
      return null;
    }
    return (
      <div className="confirm-delete-card">
        <div className="message">
          <div>
            <Icon type="exclamation-circle" theme="twoTone" twoToneColor="#faad14" />
            &nbsp; Are you sure to end this call?
          </div>
        </div>
        <div className="action-buttons">
          <Button type="primary" size="small" onClick={() => this.handleClickConfirmEndCall(_id)}>
            Yes
          </Button>
          <Button type="default" size="small" onClick={() => this.handleClickCancelEndCall(_id)}>
            No
          </Button>
        </div>
      </div>
    );
  }

  renderCallItem(call) {
    const { authStore } = this.props;
    const { _id, caller, receivers, createdAt } = call;
    const [{ name: receiverName, image: receiverImage }] = receivers;
    const { _id: callerId, name: callerName, image: callerImage } = caller;
    const accountId = authStore.myProfile._id;
    let name = receiverName;
    let image = receiverImage;
    if (accountId !== callerId) {
      name = callerName;
      image = callerImage;
    }

    return (
      <CallItemWrapper key={_id}>
        <div className="call-item-info">
          <div className="avatar">
            <Avatar size="medium" src={image.thumbnail} />
          </div>
          <div className="info">
            <div className="info-details">
              <div className="name">{name}</div>
              <div className="action-buttons">
                <Button type="danger" ghost onClick={() => this.handleClickEndCall(_id)}>
                  <Icon type="disconnect" />
                </Button>
                <Button type="primary" ghost onClick={() => this.handleOpenVideoCall(_id)}>
                  <Icon type="phone" />
                </Button>
              </div>
            </div>
            <div className="info-time">
              {moment(createdAt)
                .startOf('seconds')
                .fromNow()}
            </div>
          </div>
        </div>
        {this.renderConfirmDelete(_id)}
      </CallItemWrapper>
    );
  }

  renderEncounterItem(encounter) {
    const { _id, facility, patientInfo, createdAt } = encounter;
    const {
      image: { thumbnail },
      name: fName,
    } = facility;
    const { name, birthDate, sex } = patientInfo;
    const age = computeAge(birthDate);
    return (
      <CallItemWrapper key={_id}>
        <div className="call-item-info">
          <div className="avatar">
            <Avatar size="medium" src={thumbnail} />
          </div>
          <div className="info">
            <div className="info-details">
              <div className="name">{fName}</div>
              <div className="details">{`${name} | ${sex} | ${age}`}</div>
              <div className="action-buttons">
                <Button type="danger" ghost onClick={() => this.handleClickEndCall(_id)}>
                  <Icon type="disconnect" />
                </Button>
                <Button type="primary" ghost onClick={() => this.handleClickOpenInfo(_id)}>
                  <Icon type="line-chart" />
                </Button>
                <Button type="primary" ghost onClick={() => this.handleOpenVideoCall(_id)}>
                  <Icon type="phone" />
                </Button>
              </div>
            </div>
            <div className="info-time">
              {moment(createdAt)
                .startOf('seconds')
                .fromNow()}
            </div>
          </div>
        </div>
        {this.renderConfirmDelete(_id)}
      </CallItemWrapper>
    );
  }

  renderCalls() {
    const { calls, encounters, isLoadingCalls, isLoadingEncounters } = this.state;
    if (isLoadingEncounters || isLoadingCalls) {
      return (
        <div className="text-center">
          <Spacing size={20} />
          <Icon type="loading" style={{ fontSize: '20px' }} spin />
        </div>
      );
    }

    if (encounters.length === 0 && calls.length === 0) {
      return (
        <div className="text-center">
          <Icon type="like" theme="twoTone" style={{ fontSize: '40px' }} />
          <Spacing size={20} />
          <Alert
            type="info"
            message="You're all caught up!"
            description="You do not have pending calls."
          />
        </div>
      );
    }

    // Sort calls and encounters by createdAt
    const callsAndEncounters = [...calls, ...encounters].sort((c1, c2) => {
      if (c1.createdAt <= c2.createdAt) {
        return 1;
      }
      return -1;
    });

    return callsAndEncounters.map(c => {
      if (c.caller) {
        return this.renderCallItem(c);
      }
      return this.renderEncounterItem(c);
    });
  }

  renderOption = ({ _id, name, isOnline }) => {
    let status = <Badge status="default" />;
    if (isOnline) {
      status = <Badge status="success" />;
    }
    return (
      <Select.Option key={_id} value={_id} title={name}>
        <div id={`calls-list-item-${_id}`}>
          {status}
          {name}
        </div>
      </Select.Option>
    );
  };

  renderStartCallErrorMessage() {
    const { startCallErrorMessage } = this.state;
    if (startCallErrorMessage) {
      return (
        <>
          <Spacing size={10} />
          <Alert type="error" message={startCallErrorMessage} />
        </>
      );
    }
    return null;
  }

  render() {
    const { filteredContacts, isLoadingStartCall, value } = this.state;

    return (
      <DropdownListWrapper id="contacts-list">
        <div style={{ display: 'flex' }}>
          <Select
            showSearch
            allowClear
            onChange={this.handleChange}
            placeholder="Select contact"
            optionFilterProp="children"
            filterOption={false}
            onSearch={debounce(this.handleSearch, SELECT_REFETCH_DELAY)}
            style={{ width: '100%' }}
            value={value}
          >
            {filteredContacts.map(c => this.renderOption(c))}
          </Select>
          <Button
            icon="phone"
            type="primary"
            onClick={this.handleClickCall}
            style={{ marginLeft: '10px' }}
            disabled={isLoadingStartCall}
          >
            Call
          </Button>
        </div>
        {this.renderStartCallErrorMessage()}
        <Spacing size={20} />
        {this.renderCalls()}
      </DropdownListWrapper>
    );
  }
}

export default CallsDropdown;
